import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDependency } from '@zillow/use-dependency';

var IntersectionObserverContext = React.createContext(null);

function useVisibility(observee, disabled, options) {
  var observer = useContext(IntersectionObserverContext);

  var _useState = useState(observer ? 0 : 1),
      _useState2 = _slicedToArray(_useState, 2),
      visibleRatio = _useState2[0],
      setVisibleRatio = _useState2[1];

  useEffect(function () {
    if (observer) {
      var target = observee.current;

      if (target) {
        var manager = observer;

        if (!disabled) {
          manager.observe(target, function (e) {
            setVisibleRatio(e.intersectionRatio);
          }, options);
          return function () {
            manager.unobserve(target);
          };
        }

        manager.unobserve(target);
      }
    }

    return undefined;
  }, [observee, observer, disabled, options]);
  return {
    isVisible: visibleRatio > 0,
    visibleRatio: visibleRatio
  };
}

function useHasBeenVisible(observee, disabled, options) {
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      hasBeenVisible = _useState2[0],
      setHasBeenVisible = _useState2[1];

  var _useVisibility = useVisibility(observee, disabled || hasBeenVisible, options),
      isVisible = _useVisibility.isVisible;

  useEffect(function () {
    if (isVisible) {
      setHasBeenVisible(true);
    }
  }, [hasBeenVisible, isVisible]);
  return hasBeenVisible;
}

function generatePseudoObserver(id, root, defaultThreshold, defaultMargin) {
  var observers = new Map();
  var observees = new Map();

  var intersectionHandler = function intersectionHandler(entries) {
    entries.forEach(function (entry) {
      if (observees.has(entry.target)) {
        observees.get(entry.target)(entry);
      }
    });
  };

  var getObserverKey = function getObserverKey(options) {
    return JSON.stringify(options, null, null);
  };

  var ensureObserver = function ensureObserver(options) {
    var key = getObserverKey(options);

    if (observers.has(key)) {
      return observers.get(key);
    }

    var observer = new IntersectionObserver(intersectionHandler, {
      threshold: options.threshold,
      rootMargin: options.margin,
      root: root
    });
    observers.set(key, observer);
    return observer;
  };

  var observe = function observe(target, callback, options) {
    if (target) {
      var resolvedOptions = resolveOptions(defaultMargin, defaultThreshold, options);
      var observer = ensureObserver(resolvedOptions);
      observees.set(target, callback);
      observer.observe(target);
    }
  };

  var unobserve = function unobserve(target) {
    if (target && observers.size) {
      observers.forEach(function (observer) {
        observer.unobserve(target);
      });
      observees.delete(target);
    }
  };

  var cleanup = function cleanup() {
    observees.forEach(function (_ref) {
      var target = _ref.target;
      unobserve(target);
    });
    observees.clear();
    observers.clear();
  };

  return {
    id: id,
    observe: observe,
    unobserve: unobserve,
    cleanup: cleanup
  };
}

function resolveOptions(defaultMargin, defaultThreshold, options) {
  var margin = defaultMargin;
  var threshold = defaultThreshold;

  if (options) {
    margin = options.margin || margin;
    threshold = options.threshold || threshold;
  }

  return {
    margin: margin,
    threshold: threshold
  };
}

var intersectionObserverDependency = {
  name: 'Intersection Observer',
  verifyRequired: function verifyRequired(scope) {
    return !(typeof scope === 'undefined' || 'IntersectionObserver' in scope && 'IntersectionObserverEntry' in scope && 'intersectionRatio' in scope.IntersectionObserverEntry.prototype);
  },
  loadDependency: function loadDependency() {
    return import(
    /* webpackChunkName: "intersection-observer" */
    'intersection-observer');
  }
};

function IntersectionRoot(_ref) {
  var id = _ref.id,
      margin = _ref.margin,
      threshold = _ref.threshold,
      children = _ref.children,
      disabled = _ref.disabled,
      observeBody = _ref.observeBody,
      ignoreParent = _ref.ignoreParent,
      forwardedRef = _ref.forwardedRef;
  var customRef = useRef();
  var rootRef = forwardedRef || customRef; // Check if the element this root is monitoring has been in view.
  // This is useful for nested root as child roots won't notify their children of
  // visibility until this element has become visible itself.

  var hasBeenInView = useHasBeenVisible(rootRef);
  var emptyManager = useMemo(function () {
    return {
      id: id,
      observe: function observe() {},
      unobserve: function unobserve() {}
    };
  }, [id]);

  var _useDependency = useDependency(intersectionObserverDependency),
      _useDependency2 = _slicedToArray(_useDependency, 1),
      hasIntersectionObserver = _useDependency2[0];

  var _useState = useState(emptyManager),
      _useState2 = _slicedToArray(_useState, 2),
      observerManager = _useState2[0],
      setObserverManager = _useState2[1];

  var shouldRegister = hasIntersectionObserver && !disabled && (hasBeenInView || ignoreParent);
  useEffect(function () {
    if (shouldRegister && (rootRef.current || observeBody)) {
      var manager = generatePseudoObserver(id, observeBody ? null : rootRef.current, threshold, margin);
      setObserverManager(manager);
      return function () {
        manager.cleanup();
        setObserverManager(emptyManager);
      };
    }

    setObserverManager(emptyManager);
    return undefined;
  }, [threshold, margin, rootRef, observeBody, shouldRegister, setObserverManager, emptyManager, id]);

  if (disabled) {
    return children;
  }

  return React.createElement(IntersectionObserverContext.Provider, {
    value: observerManager
  }, React.cloneElement(children, {
    ref: rootRef
  }));
}

IntersectionRoot.propTypes = process.env.NODE_ENV !== "production" ? {
  margin: PropTypes.string,
  threshold: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
  children: PropTypes.element.isRequired,
  // eslint-disable-next-line zillow/react/require-default-props
  disabled: PropTypes.bool,
  // eslint-disable-next-line zillow/react/require-default-props
  observeBody: PropTypes.bool,
  // eslint-disable-next-line zillow/react/require-default-props
  ignoreParent: PropTypes.bool,
  id: PropTypes.string
} : {};
IntersectionRoot.defaultProps = {
  margin: '0%',
  threshold: 0,
  id: ''
}; // Forwarding to support the use case of PagedContainer where this needs to inherit the ref of
// the parent component.

var ForwardIntersectionRoot = React.forwardRef(function (props, ref) {
  return React.createElement(IntersectionRoot, _extends({}, props, {
    forwardedRef: ref
  }));
});
ForwardIntersectionRoot.displayName = 'IntersectionRoot'; // istanbul ignore else

if (process.env.NODE_ENV === 'test') {
  Object.defineProperty(ForwardIntersectionRoot, 'testDependency', {
    value: Object.freeze(intersectionObserverDependency)
  });
}

export { ForwardIntersectionRoot as IntersectionRoot, useHasBeenVisible, useVisibility };
